/**
 * Admin merchant orders view.
 *
 * Closes gap #1 from the admin-vs-merchant audit — full paginated
 * order list for one merchant + single-order detail drawer with the
 * payment gateway attribution that the merchant sees but admins
 * previously didn't. Powers support / dispute / compliance workflows
 * that previously required asking the merchant for order details.
 *
 * Route: /merchants/:merchantId/orders  (mounted in App.js under the
 *                                        admin RequireAdmin + Shell)
 * Perm gate: orders.read (adminRbacPrisma).
 *
 * CSS scoping: `mo-*` prefix per CLAUDE.md so nothing collides with
 * theme.css.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './MerchantOrders.css';

const api = (path, opts = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL || '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
};

const STATUS_COLORS = {
  PENDING:    '#d97706', CONFIRMED: '#0d7463', PROCESSING: '#0891b2',
  SHIPPED:    '#7c3aed', DELIVERED: '#059669', CANCELLED:  '#dc2626',
  REFUNDED:   '#6b7280',
};
const PAYMENT_COLORS = {
  PAID:    '#059669', UNPAID:  '#d97706',
  PARTIAL: '#0891b2', REFUNDED: '#6b7280',
};

const CHANNELS = ['STOREFRONT', 'POS', 'WHATSAPP', 'MANUAL'];
const STATUSES = ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'];
const PAYMENT_STATUSES = ['UNPAID', 'PARTIAL', 'PAID', 'REFUNDED'];
const PAYMENT_METHODS = ['PAYSTACK', 'FLUTTERWAVE', 'MONNIFY', 'CASH', 'CARD', 'BANK_TRANSFER', 'COD', 'WALLET'];

function fmtNaira(v, currency) {
  const n = Number(v) || 0;
  const symbol = (!currency || currency === 'NGN') ? '₦' : `${currency} `;
  return `${symbol}${n.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
function moneyValue(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
function firstMoney(...values) {
  for (const value of values) {
    const parsed = moneyValue(value);
    if (parsed > 0) return parsed;
  }
  return 0;
}
function getPlatformFeeQuote(source = {}) {
  return source.metadata?.platformFeeQuote
    || source.metadata?.platformFee?.result
    || source.metadata?.platformFee
    || null;
}
function getOrderFeeSplit(order = {}) {
  const quote = getPlatformFeeQuote(order) || {};
  const breakdown = order.feeBreakdown || quote.breakdown || {};
  const processorRecovery = firstMoney(order.processorRecoveryAmount, quote.processorRecovery, breakdown.processorRecovery?.amount);
  const platformFee = firstMoney(order.platformFeeAmount, quote.platformFee, breakdown.platformFee?.amount);
  const platformVat = firstMoney(order.platformFeeVatAmount, quote.platformVat, breakdown.platformVat?.amount);
  const splitTotal = processorRecovery + platformFee + platformVat;
  return {
    processorRecovery,
    platformFee,
    platformVat,
    total: splitTotal || moneyValue(order.serviceCharge),
    hasSplit: splitTotal > 0 || Boolean(order.feeModelVersion || quote.modelVersion),
    modelVersion: order.feeModelVersion || quote.modelVersion || null,
    whoPays: order.feeWhoPays || order.serviceChargeWhoPays || quote.whoPays || null,
    provider: order.processorProvider || quote.processorProvider || null
  };
}
function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return String(d); }
}
function relTime(d) {
  if (!d) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

function StatusPill({ value, kind = 'status' }) {
  const color = (kind === 'payment' ? PAYMENT_COLORS : STATUS_COLORS)[value] || '#6b7280';
  return (
    <span className="mo-pill" style={{ background: `${color}18`, color }}>
      <span className="mo-pill__dot" style={{ background: color }} />
      {value || '—'}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Main page                                                              */
/* ══════════════════════════════════════════════════════════════════════ */

export default function MerchantOrders() {
  const { merchantId } = useParams();
  const navigate = useNavigate();

  const [orders,    setOrders]    = useState([]);
  const [merchant,  setMerchant]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [detailId,  setDetailId]  = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });

  // Filters
  const [filters, setFilters] = useState({
    search: '', status: '', paymentStatus: '', channel: '', paymentMethod: '', from: '', to: '',
  });

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page',  String(opts.page  || pagination.page));
      params.set('limit', String(opts.limit || pagination.limit));
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });

      const res  = await api(`/admin/merchants/${merchantId}/orders?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setOrders(json.data || []);
      setPagination((p) => ({ ...p, ...json.pagination }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [merchantId, filters, pagination.page, pagination.limit]);

  const loadMerchant = useCallback(async () => {
    try {
      const res  = await api(`/admin/merchants/${merchantId}`);
      const json = await res.json();
      if (res.ok && json?.success) setMerchant(json.merchant || json.data || json);
    } catch { /* non-fatal */ }
  }, [merchantId]);

  useEffect(() => { load({ page: 1 }); }, [merchantId, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadMerchant(); }, [loadMerchant]);

  const setFilter = (key, value) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((cur) => ({ ...cur, [key]: value }));
  };
  const clearFilters = () => {
    setFilters({ search: '', status: '', paymentStatus: '', channel: '', paymentMethod: '', from: '', to: '' });
    setPagination((p) => ({ ...p, page: 1 }));
  };
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v && String(v).length > 0),
    [filters]
  );

  const merchantHeader = merchant
    ? (merchant.storeName || merchant.name || merchant.email || 'Merchant')
    : 'Merchant';

  return (
    <div className="mo-page">
      <header className="mo-page__head">
        <button type="button" className="mo-back" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1 className="mo-page__title">Orders — {merchantHeader}</h1>
          <p className="mo-page__subtitle">Full order history with payment gateway attribution.</p>
        </div>
      </header>

      {/* ── Filters row ────────────────────────────────── */}
      <div className="mo-filters">
        <input
          type="text"
          className="mo-input"
          placeholder="Search order #, ref, customer name / phone / email…"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <select className="mo-select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="mo-select" value={filters.paymentStatus} onChange={(e) => setFilter('paymentStatus', e.target.value)}>
          <option value="">All payment states</option>
          {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="mo-select" value={filters.channel} onChange={(e) => setFilter('channel', e.target.value)}>
          <option value="">All channels</option>
          {CHANNELS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="mo-select" value={filters.paymentMethod} onChange={(e) => setFilter('paymentMethod', e.target.value)}>
          <option value="">All gateways</option>
          {PAYMENT_METHODS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="date"
          className="mo-input"
          value={filters.from}
          onChange={(e) => setFilter('from', e.target.value)}
          title="From"
        />
        <input
          type="date"
          className="mo-input"
          value={filters.to}
          onChange={(e) => setFilter('to', e.target.value)}
          title="To"
        />
        {hasActiveFilters && (
          <button type="button" className="mo-btn mo-btn--ghost" onClick={clearFilters}>Clear</button>
        )}
        <button type="button" className="mo-btn" onClick={() => load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="mo-error">{error}</div>}

      {/* ── Table ─────────────────────────────────────── */}
      <div className="mo-table-wrap">
        <table className="mo-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Gateway</th>
              <th>Reference</th>
              <th>Customer</th>
              <th>Items</th>
              <th className="mo-num">Total</th>
              <th>Placed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && !loading && (
              <tr><td colSpan={11} className="mo-empty">No orders match these filters.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setDetailId(o.id)} className="mo-row">
                <td>
                  <strong>{o.orderNumber || o.id.slice(0, 8)}</strong>
                  {o.whatsappOrderId && <div className="mo-cell-meta">WA · {o.whatsappOrderId}</div>}
                </td>
                <td><span className="mo-chan">{o.channel || '—'}</span></td>
                <td><StatusPill value={o.status} /></td>
                <td><StatusPill value={o.paymentStatus} kind="payment" /></td>
                <td>{o.paymentMethod || '—'}</td>
                <td className="mo-mono">{o.paymentReference || '—'}</td>
                <td>
                  <div>{o.customerInfo?.name || `${o.customerInfo?.firstName || ''} ${o.customerInfo?.lastName || ''}`.trim() || '—'}</div>
                  {o.customerInfo?.phone && <div className="mo-cell-meta">{o.customerInfo.phone}</div>}
                </td>
                <td className="mo-num">{o.itemCount}</td>
                <td className="mo-num">{fmtNaira(o.total, o.currency)}</td>
                <td>{relTime(o.placedAt || o.createdAt)}</td>
                <td><button type="button" className="mo-view">View →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ────────────────────────────────── */}
      {pagination.total > 0 && (
        <div className="mo-pagination">
          <span>
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
          </span>
          <button
            type="button"
            className="mo-btn"
            disabled={pagination.page <= 1 || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page - 1 })); load({ page: pagination.page - 1 }); }}
          >Prev</button>
          <button
            type="button"
            className="mo-btn"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page + 1 })); load({ page: pagination.page + 1 }); }}
          >Next</button>
        </div>
      )}

      {/* ── Detail drawer ─────────────────────────────── */}
      {detailId && <OrderDetailDrawer orderId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Detail drawer                                                          */
/* ══════════════════════════════════════════════════════════════════════ */

function OrderDetailDrawer({ orderId, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoad(true);
      setError('');
      try {
        const res  = await api(`/admin/orders/${orderId}`);
        const json = await res.json();
        if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
        if (!cancelled) setData(json.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const order = data?.order;
  const gateway = data?.gateway;
  const merchant = data?.merchant;

  return (
    <div className="mo-drawer-scrim" onClick={onClose}>
      <aside className="mo-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="mo-drawer__head">
          <div>
            <h3>Order detail</h3>
            {order && <p className="mo-drawer__sub">{order.orderNumber} · {order.channel}</p>}
          </div>
          <button type="button" className="mo-btn mo-btn--ghost" onClick={onClose}>Close</button>
        </header>

        <div className="mo-drawer__body">
          {loading && <div className="mo-loading">Loading…</div>}
          {error && <div className="mo-error">{error}</div>}
          {order && <OrderDetail order={order} gateway={gateway} merchant={merchant} />}
        </div>
      </aside>
    </div>
  );
}

function OrderDetail({ order, gateway, merchant }) {
  const items = Array.isArray(order.items) ? order.items : [];
  const ci = order.customerInfo || {};
  const si = order.shippingInfo || {};
  const feeSplit = getOrderFeeSplit(order);

  return (
    <div className="mo-detail">
      {/* Money summary */}
      <div className="mo-detail-block">
        <div className="mo-detail-kv"><span>Subtotal</span><strong>{fmtNaira(order.subtotal, order.currency)}</strong></div>
        {Number(order.discountTotal) > 0 && (
          <div className="mo-detail-kv"><span>Discount{order.discountCode ? ` (${order.discountCode})` : ''}</span><strong>-{fmtNaira(order.discountTotal, order.currency)}</strong></div>
        )}
        {Number(order.taxTotal)      > 0 && <div className="mo-detail-kv"><span>Tax</span><strong>{fmtNaira(order.taxTotal, order.currency)}</strong></div>}
        {Number(order.shippingFee)   > 0 && <div className="mo-detail-kv"><span>Shipping</span><strong>{fmtNaira(order.shippingFee, order.currency)}</strong></div>}
        {feeSplit.total > 0 && <div className="mo-detail-kv"><span>Transaction fee</span><strong>{fmtNaira(feeSplit.total, order.currency)}</strong></div>}
        {feeSplit.hasSplit && (
          <>
            <div className="mo-detail-kv"><span>Processor recovery{feeSplit.provider ? ` (${feeSplit.provider})` : ''}</span><strong>{fmtNaira(feeSplit.processorRecovery, order.currency)}</strong></div>
            <div className="mo-detail-kv"><span>Revstar fee</span><strong>{fmtNaira(feeSplit.platformFee, order.currency)}</strong></div>
            <div className="mo-detail-kv"><span>Platform VAT</span><strong>{fmtNaira(feeSplit.platformVat, order.currency)}</strong></div>
            <div className="mo-detail-kv"><span>Fee model</span><strong>{feeSplit.modelVersion ? `v${feeSplit.modelVersion}` : 'v2'}{feeSplit.whoPays ? ` · ${String(feeSplit.whoPays).toLowerCase()} pays` : ''}</strong></div>
          </>
        )}
        <div className="mo-detail-kv mo-detail-kv--total"><span>Total</span><strong>{fmtNaira(order.total, order.currency)}</strong></div>
      </div>

      {/* Payment attribution */}
      <div className="mo-detail-block">
        <h4>Payment</h4>
        <div className="mo-detail-kv"><span>Status</span><strong><StatusPill value={order.paymentStatus} kind="payment" /></strong></div>
        <div className="mo-detail-kv"><span>Method / gateway</span><strong>{order.paymentMethod || '—'}</strong></div>
        <div className="mo-detail-kv"><span>Reference</span><strong className="mo-mono">{order.paymentReference || '—'}</strong></div>
        {order.externalPaymentRef && <div className="mo-detail-kv"><span>External ref</span><strong className="mo-mono">{order.externalPaymentRef}</strong></div>}
        {gateway && (
          <div className="mo-gateway-badge">
            <span>{gateway.displayName || gateway.name}</span>
            <span className={`mo-mini-pill mo-mini-pill--${gateway.isActive ? 'ok' : 'off'}`}>
              {gateway.isActive ? 'active' : 'inactive'}
            </span>
            <span className={`mo-mini-pill mo-mini-pill--${gateway.testMode ? 'warn' : 'ok'}`}>
              {gateway.testMode ? 'TEST' : 'LIVE'}
            </span>
          </div>
        )}
        <div className="mo-detail-kv"><span>Paid at</span><strong>{fmtDate(order.paidAt)}</strong></div>
      </div>

      {/* Fulfillment timeline */}
      <div className="mo-detail-block">
        <h4>Timeline</h4>
        <div className="mo-detail-kv"><span>Placed</span><strong>{fmtDate(order.placedAt || order.createdAt)}</strong></div>
        <div className="mo-detail-kv"><span>Paid</span><strong>{fmtDate(order.paidAt)}</strong></div>
        <div className="mo-detail-kv"><span>Fulfilled</span><strong>{fmtDate(order.fulfilledAt)}</strong></div>
        <div className="mo-detail-kv"><span>Order status</span><strong><StatusPill value={order.status} /></strong></div>
        {order.fulfillmentStatus && <div className="mo-detail-kv"><span>Fulfillment</span><strong>{order.fulfillmentStatus}</strong></div>}
        {order.cancellationReason && <div className="mo-detail-kv"><span>Cancelled because</span><strong>{order.cancellationReason}</strong></div>}
      </div>

      {/* Customer */}
      <div className="mo-detail-block">
        <h4>Customer</h4>
        <div className="mo-detail-kv"><span>Name</span><strong>{ci.name || `${ci.firstName || ''} ${ci.lastName || ''}`.trim() || '—'}</strong></div>
        <div className="mo-detail-kv"><span>Phone</span><strong className="mo-mono">{ci.phone || '—'}</strong></div>
        <div className="mo-detail-kv"><span>Email</span><strong>{ci.email || '—'}</strong></div>
      </div>

      {/* Shipping */}
      {(si.address || si.city || si.state || si.method) && (
        <div className="mo-detail-block">
          <h4>Shipping</h4>
          <div className="mo-detail-kv"><span>Method</span><strong>{si.method || '—'}</strong></div>
          {(si.address || si.city || si.state) && (
            <div className="mo-detail-kv"><span>Address</span><strong>{[si.address, si.city, si.state].filter(Boolean).join(', ')}</strong></div>
          )}
        </div>
      )}

      {/* Items */}
      <div className="mo-detail-block">
        <h4>Items ({items.length})</h4>
        {items.length === 0 && <p className="mo-detail-empty">No items on this order.</p>}
        {items.map((it, i) => {
          // Prefer variantCombination (rich object like { Size: "L", Color: "Red" })
          // to match the merchant-side format in OrderDetailsPage. Falls back
          // to variantName for older orders / MPM carts that only carry the string.
          let variantLabel = null;
          if (it.variantCombination && typeof it.variantCombination === 'object' && Object.keys(it.variantCombination).length > 0) {
            variantLabel = Object.entries(it.variantCombination)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ');
          } else if (it.variantName) {
            variantLabel = it.variantName;
          }
          return (
            <div key={i} className="mo-item-row">
              <div>
                <strong>{it.name || 'Item'}</strong>
                {variantLabel && <div className="mo-item-variant">{variantLabel}</div>}
                {it.sku && <div className="mo-cell-meta">SKU: {it.sku}</div>}
              </div>
              <div className="mo-item-price">
                <span className="mo-cell-meta">{it.quantity} × {fmtNaira(it.price, order.currency)}</span>
                <strong>{fmtNaira((Number(it.price) || 0) * (Number(it.quantity) || 0), order.currency)}</strong>
              </div>
            </div>
          );
        })}
      </div>

      {/* Refund requests */}
      {order.refundRequests?.length > 0 && (
        <div className="mo-detail-block">
          <h4>Refund requests ({order.refundRequests.length})</h4>
          {order.refundRequests.map((r) => (
            <div key={r.id} className="mo-refund-row">
              <div>
                <StatusPill value={r.status} />
                <div className="mo-cell-meta">{r.requestType} · {r.priority}</div>
              </div>
              <div className="mo-item-price">
                <strong>{fmtNaira(r.refundAmount, order.currency)}</strong>
                <span className="mo-cell-meta">{fmtDate(r.createdAt)}</span>
              </div>
              {r.refundReason && <div className="mo-refund-reason">"{r.refundReason}"</div>}
            </div>
          ))}
        </div>
      )}

      {/* Merchant */}
      {merchant && (
        <div className="mo-detail-block">
          <h4>Merchant</h4>
          <div className="mo-detail-kv"><span>Store</span><strong>{merchant.storeName || '—'}{merchant.storeSlug ? ` (/${merchant.storeSlug})` : ''}</strong></div>
          <div className="mo-detail-kv"><span>Email</span><strong>{merchant.email}</strong></div>
          <div className="mo-detail-kv"><span>Tier</span><strong>{merchant.tier || 'FREE'}</strong></div>
        </div>
      )}

      {/* Raw metadata for compliance / debugging */}
      {order.metadata && Object.keys(order.metadata).length > 0 && (
        <details className="mo-detail-block mo-raw">
          <summary>Raw metadata</summary>
          <pre>{JSON.stringify(order.metadata, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}
